/**
 * Pure helpers deciding which non-data state a chart should show, plus the copy
 * for those states.
 *
 * Design contract: /docs/adr/0012-dashboard-chart-state-parity.md. The React
 * half of this lives in @syrenis/utilities and must agree with these helpers on
 * the answer for equivalent data (AB#26801 AC 15). The two take different
 * inputs: this one takes a Chart.js payload, the React one takes Recharts' flat
 * row arrays. The case table in chart-data-state.spec.js is mirrored there.
 *
 * The copy lives here rather than in the component so a reword is a one-file
 * edit. Vue.Frontends cannot import @syrenis/utilities (separate sub-monorepo
 * and lockfile), so these strings are duplicated on the React side by hand.
 * Keep them byte-identical.
 */

/** US English, per the frontend copy convention, in both portals. */
export const CHART_EMPTY_TITLE = 'No data found'
export const CHART_EMPTY_DESCRIPTION = 'Try adjusting your filters or selecting a different date range'
export const CHART_ERROR_TITLE = 'Couldn\'t load this chart'
export const CHART_ERROR_DESCRIPTION = 'Try again, or refresh the page if the problem persists'
export const CHART_RETRY_LABEL = 'Try again'

/**
 * Height thresholds for the two decorative layers, in px.
 *
 * The cascade drops decoration before information: the ghost chart scaffold
 * goes first, then the icon, and the title and supporting text always survive.
 * Text is the part that actually tells the user what happened, so a cramped
 * chart in a compare dialog or detail modal ends up plainer, never quieter.
 *
 * The React implementation uses the same numbers, so the 200-300px charts in
 * those dialogs degrade identically in both portals.
 */
export const CHART_GHOST_MIN_HEIGHT = 260
export const CHART_ICON_MIN_HEIGHT = 180

/**
 * Ghost scaffold bar sizes, as a percentage of the plot area.
 *
 * Used as heights for vertical bars and as widths for horizontal ones: the
 * no-overlap rule is the same either way, just rotated. The three middle bars
 * are short and the long pair sits outside, so the centred message lands in
 * clear space whichever direction the bars run.
 *
 * These numbers are shared with the React implementation. Change them here and
 * you change them there, and in ADR-0012.
 */
export const CHART_GHOST_BAR_HEIGHTS = [76, 30, 22, 28, 64]

/**
 * Ghost polylines for the line and area shapes, as percentages of the plot box
 * with y measured from the top. One entry per series, most prominent first.
 *
 * Each series has its own trend and they cross. An earlier React revision
 * offset a single wiggle three times and read as one line stamped repeatedly
 * rather than as independent series, so: one rising, one roughly flat, one
 * falling.
 *
 * Every point sits below y=67, clear of the roughly 34% to 66% band the
 * centred message occupies. That is what keeps the no-overlap rule intact with
 * several lines in play, and why the lines live in the lower third rather than
 * spreading across the plot.
 */
export const CHART_GHOST_LINE_SERIES = [
	// Rising steadily.
	[
		{ x: 0, y: 88 },
		{ x: 20, y: 83 },
		{ x: 40, y: 78 },
		{ x: 60, y: 76 },
		{ x: 80, y: 71 },
		{ x: 100, y: 67 }
	],
	// Roughly flat, with a dip in the middle.
	[
		{ x: 0, y: 77 },
		{ x: 20, y: 80 },
		{ x: 40, y: 86 },
		{ x: 60, y: 83 },
		{ x: 80, y: 78 },
		{ x: 100, y: 79 }
	],
	// Falling away.
	[
		{ x: 0, y: 70 },
		{ x: 20, y: 75 },
		{ x: 40, y: 81 },
		{ x: 60, y: 86 },
		{ x: 80, y: 90 },
		{ x: 100, y: 93 }
	]
]

/** How many ghost line series are defined, so callers can clamp to it. */
export const CHART_GHOST_MAX_LINE_SERIES = CHART_GHOST_LINE_SERIES.length

/**
 * Ghost ring geometry for the pie shape, as percentages of the viewBox.
 *
 * A ring rather than a filled pie with slice dividers: a pie's slices converge
 * on the centre, which is exactly where the message sits, so dividers would cut
 * through the copy. The inner radius is wide enough for the message to sit
 * inside it.
 */
export const CHART_GHOST_RING = { outerRadius: 42, innerRadius: 30 }

/**
 * The shapes the ghost scaffold can draw.
 *
 * Pick the shape from what the chart actually renders, not from the component
 * that draws it. A Chart.js bar chart with `indexAxis: 'y'` draws horizontally,
 * and a line dataset with `fill` set draws as an area.
 */
export const CHART_GHOST_SHAPES = ['bar', 'bar-horizontal', 'line', 'area', 'pie']

/**
 * Whether a single Chart.js data point counts as something worth plotting.
 *
 * Zero does not count: a chart of nothing but zeroes renders axes and a flat
 * line, which reads as broken rather than as "nothing happened this period".
 *
 * Numeric strings do count, because Chart.js renders them. Note that blind
 * coercion would be wrong here: Number('') and Number([]) are both 0 and
 * Number(true) is 1, so empty strings, booleans and empty arrays have to be
 * rejected before any coercion happens.
 *
 * @param {unknown} value - A single entry from a dataset's `data` array
 * @returns {boolean} True if the value is a finite, non-zero number
 */
function isPlottableValue (value) {
	if (typeof value === 'number') {
		return Number.isFinite(value) && value !== 0
	}

	if (typeof value === 'string') {
		const trimmed = value.trim()
		if (trimmed === '') {
			return false
		}
		const numeric = Number(trimmed)
		return Number.isFinite(numeric) && numeric !== 0
	}

	return false
}

/**
 * Whether a Chart.js payload has anything the user would actually see plotted.
 *
 * Returns false for absent or malformed payloads, for an absent or empty
 * `datasets` array, and for datasets whose values are entirely zero, nullish or
 * non-numeric.
 *
 * Labels are required by default, because bar, line and radar position their
 * points along a category scale and so draw nothing without them. Slice-style
 * charts (pie, doughnut, polar-area) size their arcs from the values alone and
 * use labels only for legend and tooltip text, so those callers pass
 * `requireLabels: false`. Getting this wrong in the strict direction would
 * report "no data" over a chart that does in fact draw, which is the opposite
 * of what this helper is for.
 *
 * Only numeric and numeric-string data points are understood. Chart.js also
 * accepts `{ x, y }` objects (scatter, bubble) and `[min, max]` tuples
 * (floating bars); those count as absent here. None of the six wrappers this
 * gates use them. Widen this before pointing such a chart at it.
 *
 * @param {Object} [chartData] - Chart.js format data
 * @param {string[]} [chartData.labels] - Category or time-series labels
 * @param {Object[]} [chartData.datasets] - Datasets, each with a `data` array
 * @param {Object} [options]
 * @param {boolean} [options.requireLabels=true] - False for slice-style charts
 * @returns {boolean} True if at least one finite non-zero value would render
 *
 * @example
 * hasPlottableData({ labels: ['Jan'], datasets: [{ data: [0] }] }) // false
 * hasPlottableData({ labels: ['Jan'], datasets: [{ data: [7] }] }) // true
 * hasPlottableData({ datasets: [{ data: [7] }] }, { requireLabels: false }) // true
 */
export function hasPlottableData (chartData, { requireLabels = true } = {}) {
	if (!chartData || typeof chartData !== 'object' || Array.isArray(chartData)) {
		return false
	}

	const { labels, datasets } = chartData

	if (requireLabels && (!Array.isArray(labels) || labels.length === 0)) {
		return false
	}

	if (!Array.isArray(datasets) || datasets.length === 0) {
		return false
	}

	return datasets.some(dataset =>
		Array.isArray(dataset?.data) && dataset.data.some(isPlottableValue)
	)
}

/**
 * Resolves which state a chart should render.
 *
 * Precedence is `loading`, then `error`, then `empty`, then `populated`. Error
 * deliberately beats both empty and populated: a failed request must never be
 * reported as "no data found", which is the ambiguity AB#26801 exists to fix. A
 * host that wants to keep stale data on screen through a failed refetch should
 * withhold `error` and surface the failure itself.
 *
 * Called with nothing, this returns `empty` rather than `populated`, so a
 * miswired caller shows an honest message instead of a blank canvas.
 *
 * @param {Object} [input]
 * @param {boolean} [input.loading] - Whether a request is in flight
 * @param {unknown} [input.error] - Any truthy value, including a bare message
 * @param {Object} [input.chartData] - Chart.js format data
 * @param {boolean} [input.requireLabels=true] - False for slice-style charts
 * @returns {'loading'|'error'|'empty'|'populated'} The state to render
 */
/**
 * Resolves what the empty or error state actually shows: which copy, which
 * decorative layers survive at this height, and whether a retry is offered.
 *
 * Pure and shared so the parts of the design contract most likely to drift
 * (the default copy and the degradation cascade) are asserted by tests rather
 * than buried in a template. The icon glyph is deliberately not resolved here:
 * the two portals draw from different icon libraries, so only the meaning is
 * shared, not the name. See ADR-0012.
 *
 * @param {Object} input
 * @param {'loading'|'empty'|'error'} input.variant - The state being rendered
 * @param {number} input.height - Host chart height in px
 * @param {string} [input.title] - Overrides the default title
 * @param {string} [input.description] - Overrides the default description;
 *   pass an empty string to suppress it
 * @returns {{title: string, description: string, showDescription: boolean,
 *   showIcon: boolean, showGhost: boolean, showRetry: boolean}}
 */
export function resolveStatePresentation ({ variant, height, title = null, description = null }) {
	const isError = variant === 'error'

	const resolvedTitle = title !== null
		? title
		: (isError ? CHART_ERROR_TITLE : CHART_EMPTY_TITLE)

	const resolvedDescription = description !== null
		? description
		: (isError ? CHART_ERROR_DESCRIPTION : CHART_EMPTY_DESCRIPTION)

	return {
		title: resolvedTitle,
		description: resolvedDescription,
		// Not height-gated. Supporting text is the informative part and always
		// survives; only an explicit empty string suppresses it.
		showDescription: !!resolvedDescription,
		showIcon: height >= CHART_ICON_MIN_HEIGHT,
		showGhost: height >= CHART_GHOST_MIN_HEIGHT,
		showRetry: isError
	}
}

export function resolveChartState ({ loading, error, chartData, requireLabels = true } = {}) {
	if (loading) {
		return 'loading'
	}

	if (error) {
		return 'error'
	}

	return hasPlottableData(chartData, { requireLabels }) ? 'populated' : 'empty'
}
